import ReactGA from 'react-ga4';
import User from '@/models/User';
import { isLocalAnalyticsHost } from '@/helpers/isLocalAnalyticsHost';
import { resolveInfoGaMeasurementId } from '@/helpers/gaHostPolicy';

/**
 * Custom hook for Google Analytics integration
 * Provides functions to track page views, events, and user identification
 *
 * SPR-1206 C: info uses its own measurement id (env). The www acquisition id
 * G-ELLRDSWMY7 is blocked on info/info2 — no cross-domain linker, no shared property.
 */
export const useGoogleAnalytics = () => {
  // נקרא בתוך כל פונקציה ולא נלכד ברנדר: האתחול קורה רק אחרי הסכמה (HAT-1081),
  // ולכידה מוקדמת הייתה משאירה את identify/track עם ערך מיושן באותו מחזור.
  const isInitialized = ReactGA.isInitialized;

  const initialize = (measurementId: string) => {
    if (typeof window !== 'undefined' && isLocalAnalyticsHost(window.location.hostname)) return;
    if (import.meta.env.DEV) return;

    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const allowedId = resolveInfoGaMeasurementId(measurementId, hostname);
    if (!allowedId || ReactGA.isInitialized) return;

    ReactGA.initialize(allowedId, {
      gtagOptions: {
        send_page_view: false,
      },
    });
  };

  const trackPageView = (path: string, title?: string) => {
    if (!ReactGA.isInitialized) return;

    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });
  };

  // HAT-1081: לא נשלח ל-GA שום מזהה משתמש - גם לא פסאודונימי. זו ההחלטה שהתקבלה
  // ב-master והיא מחמירה יותר מזו שתכננתי כאן: מי שצריך לזהות משתמש הוא PostHog,
  // ול-GA מספיק פילוח ברמת האתר. אסור להוסיף לכאן userId או שדות מזהים.
  const identifyUser = (_user: User) => {
    if (!ReactGA.isInitialized) return;

    ReactGA.set({
      user_properties: {
        site: window.location.host,
      },
    });
  };

  const trackEvent = (
    category: string,
    action: string,
    label?: string,
    value?: number,
    additionalParams?: Record<string, unknown>
  ) => {
    if (!ReactGA.isInitialized) return;

    ReactGA.event({
      category,
      action,
      label,
      value,
      ...additionalParams,
    });
  };

  const resetUser = () => {
    if (!ReactGA.isInitialized) return;

    // GA4 doesn't have a direct reset, but we can clear user properties
    ReactGA.set({
      userId: undefined,
      user_properties: undefined,
    });
  };

  return {
    initialize,
    trackPageView,
    identifyUser,
    trackEvent,
    resetUser,
    isInitialized,
  };
};
